/**
 * The active-route count of a Spraywall wall: the number on the picker card,
 * in the wall page's subtitle, on the `Utak` option of its `Segmented`, and —
 * per version — on a row of the old-versions modal. Also the picker card's
 * "N new this week" count. docs/spec/21j-spraywall-tab-navigation.md,
 * "The wall card — SprayWallCard" and "Wall view (shell)".
 *
 * One `getCountFromServer` (one billed read, no document downloaded):
 * `sprayProblems where wallId == W, versionId == V, status == "active",
 * visibility == "public"`, served by the existing (wallId, versionId, status,
 * visibility) index. Never subscribed: the number is taken once per opening.
 * The "new this week" count adds a `createdAt >=` range to the same equalities,
 * which needs its own composite index (the emulator never asks for it).
 */
import { useEffect, useState } from "react";
import { collection, query, where, getCountFromServer } from "firebase/firestore";
import { db } from "../firebase";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** The count for one wall version: a number, or null on any error / without a version. */
export function fetchActiveRouteCount({ gymId, wallId, versionId }) {
  if (!gymId || !wallId || !versionId) return Promise.resolve(null);
  return Promise.resolve()
    .then(() => getCountFromServer(query(
      collection(db, "gyms", gymId, "sprayProblems"),
      where("wallId", "==", wallId),
      where("versionId", "==", versionId),
      where("status", "==", "active"),
      where("visibility", "==", "public"),
    )))
    .then((snap) => {
      const n = snap?.data?.()?.count;
      return Number.isFinite(n) ? n : null;
    }, () => null);
}

/**
 * The routes of one wall version set in the last 7 days (same filters as the
 * active count plus `createdAt`). A number, or 0 on any error / without a
 * version: the picker leaves the "N new this week" item out for 0, so a failed
 * read never shows a wrong figure.
 */
export function fetchNewThisWeekCount({ gymId, wallId, versionId, now = Date.now() }) {
  if (!gymId || !wallId || !versionId) return Promise.resolve(0);
  return Promise.resolve()
    .then(() => getCountFromServer(query(
      collection(db, "gyms", gymId, "sprayProblems"),
      where("wallId", "==", wallId),
      where("versionId", "==", versionId),
      where("status", "==", "active"),
      where("visibility", "==", "public"),
      where("createdAt", ">=", new Date(now - WEEK_MS)),
    )))
    .then((snap) => {
      const n = snap?.data?.()?.count;
      return Number.isFinite(n) ? n : 0;
    }, () => 0);
}

/**
 * The wall page's count: `undefined` while counting (the caller leaves the
 * number out), `null` on an error or without `currentVersionId` ("–"), else
 * the number. One read per mount and per live version (a publish re-counts).
 */
export function useSprayActiveRouteCount({ gymId, wall, enabled = true }) {
  const wallId = wall?.id || null;
  const versionId = wall?.currentVersionId || null;
  const active = !!enabled && !!gymId && !!wallId;
  const key = active ? `${gymId}/${wallId}@${versionId || ""}` : null;
  const [result, setResult] = useState({ key: null, count: undefined });

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    fetchActiveRouteCount({ gymId, wallId, versionId }).then((count) => {
      if (!cancelled) setResult({ key, count });
    });
    return () => { cancelled = true; };
  }, [active, gymId, wallId, versionId, key]);

  return active && result.key === key ? result.count : undefined;
}
